


import { Fragment, Suspense, Dispatch, SetStateAction, useCallback, useState, useEffect, memo, lazy } from 'react';
import { ILanguageSelectControllerModel, ILanguageSelectControllerProps, ILanguageSelectType } from './interface'
import modelPlaceholderData from '../../components/languageselect/placeholderData'
import LoadingView from '../../components/common/Loading';
import type ILanguageDto from '@workspace/apiservice/src/model/LanguageDto.if';
import type IMapping from "./mapping";
import { injectLog } from '../../interface/util';
import { ILanguageSelectViewModel } from '../../components/languageselect/interface';
import { getProjectOverrides } from '../../interface/overrides';
const isEqual = require("react-fast-compare");
const MainView = lazy(() => import('../../components/languageselect'));
const loadMapper = (): Promise<typeof IMapping> => {
    return new Promise(function(resolve, reject) {
        import('./mapping')
            .then(controller => resolve(controller.default))
            .catch(error => reject(error));
    });
};


interface ILanguageSelectController {
    model: ILanguageSelectControllerModel;
    type: ILanguageSelectType;
    titleCyclerTimeout: ReturnType<typeof setTimeout> | null;
    isLoading: boolean;
}

const controllerPlaceholderData = (): ILanguageSelectController => ({
    model: {
        ...modelPlaceholderData(),
        cycler: null,
        activeTitle: () => '',
        nextTitle: () => '',
        delayBetweenChangingTitle: -1,
    
    },
    type: ILanguageSelectType.List,
    titleCyclerTimeout: null,
    isLoading: true,
})

/* Same as useState, except 3 return-value allow for partial state updates */
function ReactUseStateWithUpdate<S>(initialState: S | (() => S)): [S, Dispatch<SetStateAction<S>>, Dispatch<Partial<S>>] {
    const [getVal, setVal] = useState<S>(initialState);
    const updateVal = useCallback((partial: Partial<S>) => setVal((sPrev) => ({...sPrev, ...partial})), []);
    return [getVal, setVal, updateVal]
}

function View(props: ILanguageSelectControllerProps): JSX.Element {
    const log = injectLog();

    const [state, setState, updateState] = ReactUseStateWithUpdate<ILanguageSelectController>(controllerPlaceholderData())

    const onLanguageSelect = useCallback((language: ILanguageDto) => props.didRequestTourSelect(language.id), [props.didRequestTourSelect]);

    const setupUpdates = (delayBetweenChangingTitle: number = -1) => {
        if ( state.titleCyclerTimeout ) {
            clearInterval(state.titleCyclerTimeout);
        }

        if ( delayBetweenChangingTitle > 0 ) {
            const titleCyclerTimeout = setInterval(_ => {
                log.verbose('Updating LS title, old:' + state.model.title.text);
                setState(stateIn => {
                    const stateOut: ILanguageSelectController = {...stateIn};
                    stateOut.model = {...stateIn.model};
                    stateOut.model.title = {...stateIn.model.title};
                    stateOut.model.title.text = stateIn.model.nextTitle() || stateIn.model.title.text;
                    return stateOut;
                });
            }, delayBetweenChangingTitle);
    
            updateState({titleCyclerTimeout,})
        } 
    }

    const clearUpdates = () => {
        log.verbose('Clearing up resources');
        if ( state.titleCyclerTimeout ) {
            clearInterval(state.titleCyclerTimeout);
        }
        updateState({ titleCyclerTimeout: null })
    };

    useEffect(() => {
        clearUpdates();
        loadMapper()
            .then(function(mapper) {
                return mapper(props.route, onLanguageSelect);
            })        
            .then(function([model, type]) {
                updateState({
                    model: getProjectOverrides<ILanguageSelectControllerModel>('ILanguageSelectViewModel', model),
                    type,
                    isLoading: false,
                });
                setupUpdates(model.delayBetweenChangingTitle);
            })
            .then(_ => log.info('Loaded LanguageSelect'))
            .catch(error => log.error(`Failed to load LanguageSelect ${error}`));

        return () => { clearUpdates(); };
    }, [props.route])

    useEffect(() => {
        document.title = state.model.title.text;
    }, [state.model.title.text]);

    return (
        <Fragment key="LanguageSelect">
            <LoadingView styleOverrides={{
                opacity: state.isLoading ? 1 : 0,
                pointerEvents: state.isLoading ? 'auto' : 'none',
            }} />
            <Suspense fallback={null}>
                { !state.isLoading && <MainView key="LanguageSelect" type={state.type} model={state.model} /> }
            </Suspense>
        </Fragment>
    );
}

function propsEqual(prev: ILanguageSelectControllerProps, next: ILanguageSelectControllerProps): boolean {
    const match = isEqual(prev, next);
    return match;
}

export default memo(View, propsEqual) as typeof View;
